import React, { useState, useRef, useContext, useCallback } from "react";
import { Container, Grid, Paper, Tabs, Tab, Typography, Box } from "@mui/material";
import UserPageGeneral from "./userPageGeneral"; 
import UserPageChangepassword from "./userPageChangepassword";
import { useIsMobile } from "../../../util/utils";
import { UserContext } from "../../../components/auth/auth-context";
import { setSelectedBusiness } from "../../../redux/actions/businessActions";
import { useSelectBusinesses } from "../../../redux/store";
import useLoadReduxStoreData from "../../../redux/reducers/loadReduxStoreData";
import { BusinessQueryNames } from "../../../graphql/queries";
import { UPDATE_PROFILE } from "../../../graphql/mutations";
import { useMutation } from "@apollo/client";
import { useDispatch } from "react-redux";
import { useTokenLoader } from "../../../util/tokenLoader";

const headerTitles = [
  "General Profile", // General tab 
  "Change Your Password" // Change Password tab
];

const UserPage = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const isMobile = useIsMobile();
  const { providerUser } = useContext(UserContext);
  const token = providerUser;
  useLoadReduxStoreData(BusinessQueryNames.GET_MY_BUSINESSES);
  const businesses = useSelectBusinesses();
  const [update] = useMutation(UPDATE_PROFILE);
  useTokenLoader();

  // State values for profile fields
  const [username, setUsername] = useState(token.username);
  const [email, setEmail] = useState(token.email); 
  const [desc, setDesc] = useState(token.desc);
  const [defaultBusinessId, setDefaultBusinessId] = useState(token.defaultBusinessId);
  const [selectedTimeZoneId, setSelectedTimeZoneId] = useState(token.timeZone?.id);

  // Separate updated flags for different sections
  const [generalUpdated, setGeneralUpdated] = useState(false); 
  const [errors, setErrors] = useState("");  

  const handleSelectDefaultBusinessChange = async (e) => {
    const selectedBusinessId = e.target.value;
    const selectedBusiness = businesses.find(
      (business) => business.businessBasic.id === selectedBusinessId
    );
    if (selectedBusiness) {
      try {
        setGeneralUpdated(true);
        setDefaultBusinessId(selectedBusiness.businessBasic.id);
        // Simulate backend update
        handleSubmit();
        dispatch(setSelectedBusiness(selectedBusinessId));
      } catch (error) {
        console.error("Failed to update default business:", error);
      }
    }
  };

  const handleSubmit = () => { 
    setGeneralUpdated(false); 
    if (token) { 
      update({
        variables: {
          userId: Number(token.userId),
          email,
          username, 
          hours24: token.hours24,
          timeZoneId: Number(selectedTimeZoneId),
          desc,
          planId: token.planId,
          calendar: token.calendar,
          defaultBusinessId: Number(defaultBusinessId)
        },
      })
        .then((newToken) => {
          setErrors("");
          console.log("New token:", newToken);
          localStorage.setItem("token", newToken.data.updateProfile.token); 
        })
        .catch((error) => {
          if (JSON.stringify(error).includes("jwt")) {
            // Handle specific error here if needed
          } 
        });
    }
  };

  const handleTimezoneSelect = useCallback((tz) => {
    if (tz.timezone !== token?.timeZone?.timezone) {
      setSelectedTimeZoneId(tz.id);
      setGeneralUpdated(true);
    }
  }, [selectedTimeZoneId]);

  const notificationRef = useRef();

  const handleTabChange = (e, newValue) => {
    setTabValue(newValue);
  };

  return (
    <>
      <Box>
        <Typography
          variant="h5"
          className="input-label"
          sx={{
            fontSize: {
              xs: '1.5rem',
              sm: '2rem',
            },
            textAlign: 'center',  // Ensure text is centered 
          }}
        >
          {headerTitles[tabValue]}
        </Typography>
      </Box>
      <Container maxWidth="lg" sx={{ marginTop: 8 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={isMobile ? 12 : 3}>
            <Paper>
              <Tabs
                orientation={isMobile ? "horizontal" : "vertical"}
                value={tabValue}
                onChange={handleTabChange}
                aria-label="User settings tabs"
              >
                <Tab label="General" /> 
                <Tab label="Change Password" />
              </Tabs>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={isMobile ? 12 : 9}>
            <Paper>
              {tabValue === 0 && (
                <UserPageGeneral
                  defaultBusinessId={defaultBusinessId}
                  defaultBusinessLoading={false}
                  defaultBusinessError={null}
                  businesses={businesses}
                  handleSelectDefaultBusinessChange={handleSelectDefaultBusinessChange}
                  username={username}
                  setUsername={setUsername}
                  setUpdated={setGeneralUpdated}  // Pass general update setter
                  email={email}
                  selectedTimeZoneId={selectedTimeZoneId}
                  token={token}
                  handleTimezoneSelect={handleTimezoneSelect}
                  handleSubmit={handleSubmit}
                  updated={generalUpdated} 
                  notificationRef={notificationRef}
                  desc={desc}
                  setDesc={setDesc}
                  errors={errors}
                />
              )}

              {tabValue === 1 && <UserPageChangepassword error={false} setError={() => { }} />}
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default UserPage;
